'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

type SupplierStatus = 'live' | 'ready' | 'awaiting_credentials' | 'inactive';
type APIHealth = 'healthy' | 'degraded' | 'offline' | 'pending';

interface SupplierEntry {
  id: string;
  name: string;
  category: string;
  status: SupplierStatus;
  apiHealth: APIHealth;
  credentialStatus: 'configured' | 'missing' | 'invalid';
  lastSync: string;
  catalogueSize: number;
  syncProgress: number;
  validationScore: number;
  bookingsToday: number;
  revenueToday: number;
  errorCount: number;
  uptime: number;
  responseMs: number;
  alerts: { level: 'warning' | 'critical'; message: string }[];
}

const SUPPLIERS: SupplierEntry[] = [
  { id: 'viator', name: 'Viator', category: 'Activities', status: 'live', apiHealth: 'healthy', credentialStatus: 'configured', lastSync: '5 min ago', catalogueSize: 12847, syncProgress: 100, validationScore: 98, bookingsToday: 23, revenueToday: 18400, errorCount: 0, uptime: 99.8, responseMs: 210, alerts: [] },
  { id: 'gadventures', name: 'G Adventures', category: 'Tours', status: 'live', apiHealth: 'healthy', credentialStatus: 'configured', lastSync: '12 min ago', catalogueSize: 4320, syncProgress: 100, validationScore: 97, bookingsToday: 8, revenueToday: 42000, errorCount: 1, uptime: 99.5, responseMs: 340, alerts: [{ level: 'warning', message: 'Rate limit at 78%' }] },
  { id: 'crystal', name: 'Crystal Cruises', category: 'Cruises', status: 'awaiting_credentials', apiHealth: 'pending', credentialStatus: 'missing', lastSync: 'Never', catalogueSize: 0, syncProgress: 0, validationScore: 43, bookingsToday: 0, revenueToday: 0, errorCount: 0, uptime: 0, responseMs: 0, alerts: [{ level: 'critical', message: 'API credentials not configured' }] },
  { id: 'ak', name: 'Abercrombie & Kent', category: 'Journeys', status: 'awaiting_credentials', apiHealth: 'pending', credentialStatus: 'missing', lastSync: 'Never', catalogueSize: 0, syncProgress: 0, validationScore: 47, bookingsToday: 0, revenueToday: 0, errorCount: 0, uptime: 0, responseMs: 0, alerts: [{ level: 'critical', message: 'API credentials not configured' }] },
  { id: 'up17', name: 'UP17', category: 'Multi', status: 'ready', apiHealth: 'healthy', credentialStatus: 'configured', lastSync: '1 hr ago', catalogueSize: 890, syncProgress: 100, validationScore: 91, bookingsToday: 3, revenueToday: 8200, errorCount: 0, uptime: 98.9, responseMs: 180, alerts: [] },
  { id: 'tourradar', name: 'TourRadar', category: 'Tours', status: 'awaiting_credentials', apiHealth: 'pending', credentialStatus: 'missing', lastSync: 'Never', catalogueSize: 0, syncProgress: 0, validationScore: 0, bookingsToday: 0, revenueToday: 0, errorCount: 0, uptime: 0, responseMs: 0, alerts: [{ level: 'critical', message: 'Credentials required' }] },
  { id: 'amadeus', name: 'Amadeus', category: 'Flights', status: 'awaiting_credentials', apiHealth: 'pending', credentialStatus: 'missing', lastSync: 'Never', catalogueSize: 0, syncProgress: 0, validationScore: 0, bookingsToday: 0, revenueToday: 0, errorCount: 0, uptime: 0, responseMs: 0, alerts: [{ level: 'critical', message: 'Credentials required' }] },
  { id: 'duffel', name: 'Duffel', category: 'Flights', status: 'awaiting_credentials', apiHealth: 'pending', credentialStatus: 'missing', lastSync: 'Never', catalogueSize: 0, syncProgress: 0, validationScore: 0, bookingsToday: 0, revenueToday: 0, errorCount: 0, uptime: 0, responseMs: 0, alerts: [{ level: 'critical', message: 'Credentials required' }] },
];

const STATUS_CONFIG: Record<SupplierStatus, { label: string; color: string; dot: string }> = {
  live: { label: 'Live', color: 'bg-emerald-500/15 text-emerald-400', dot: 'bg-emerald-400' },
  ready: { label: 'Ready', color: 'bg-blue-500/15 text-blue-400', dot: 'bg-blue-400' },
  awaiting_credentials: { label: 'Awaiting Credentials', color: 'bg-amber-500/15 text-amber-400', dot: 'bg-amber-400' },
  inactive: { label: 'Inactive', color: 'bg-surface-3 text-muted-foreground', dot: 'bg-surface-4' },
};

const HEALTH_CONFIG: Record<APIHealth, { label: string; color: string }> = {
  healthy: { label: 'Healthy', color: 'text-emerald-400' },
  degraded: { label: 'Degraded', color: 'text-amber-400' },
  offline: { label: 'Offline', color: 'text-red-400' },
  pending: { label: 'Pending', color: 'text-muted-foreground' },
};

const TABS = ['Overview', 'Health Monitor', 'Sync & Import', 'Error Logs', 'Performance'];

export default function SupplierOperationsCenterPage() {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedSupplier, setSelectedSupplier] = useState<SupplierEntry | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const filtered = filterStatus === 'all' ? SUPPLIERS : SUPPLIERS.filter(s => s.status === filterStatus);

  const liveCount = SUPPLIERS.filter(s => s.status === 'live').length;
  const awaitingCount = SUPPLIERS.filter(s => s.status === 'awaiting_credentials').length;
  const totalRevenue = SUPPLIERS.reduce((s, sup) => s + sup.revenueToday, 0);
  const totalBookings = SUPPLIERS.reduce((s, sup) => s + sup.bookingsToday, 0);
  const totalAlerts = SUPPLIERS.reduce((s, sup) => s + sup.alerts.length, 0);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="border-b border-border bg-surface-1">
        <div className="max-w-screen-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link href="/enterprise-admin-platform" className="text-muted-foreground hover:text-foreground transition-colors">
              <Icon name="ArrowLeftIcon" size={18} />
            </Link>
            <div>
              <h1 className="text-lg font-bold text-foreground tracking-tight">Supplier Operations Center</h1>
              <p className="text-xs text-muted-foreground">Enterprise supplier health, sync, validation, and activation controls</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs bg-emerald-500/15 text-emerald-400 px-2.5 py-1 rounded-full font-medium">{liveCount} Live</span>
            <span className="text-xs bg-amber-500/15 text-amber-400 px-2.5 py-1 rounded-full font-medium">{awaitingCount} Awaiting</span>
            {totalAlerts > 0 && <span className="text-xs bg-red-500/15 text-red-400 px-2.5 py-1 rounded-full font-medium">{totalAlerts} Alerts</span>}
          </div>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-6 py-6 space-y-6">
        {/* KPI Row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Total Suppliers', value: SUPPLIERS.length, icon: 'BuildingStorefrontIcon', color: 'text-foreground' },
            { label: 'Live', value: liveCount, icon: 'CheckCircleIcon', color: 'text-emerald-400' },
            { label: 'Awaiting Credentials', value: awaitingCount, icon: 'KeyIcon', color: 'text-amber-400' },
            { label: "Today's Bookings", value: totalBookings, icon: 'CalendarDaysIcon', color: 'text-blue-400' },
            { label: "Today's Revenue", value: `₹${(totalRevenue / 1000).toFixed(0)}K`, icon: 'BanknotesIcon', color: 'text-primary' },
          ].map(kpi => (
            <div key={kpi.label} className="bg-surface-2 border border-border rounded-xl p-4 flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg bg-surface-3 flex items-center justify-center ${kpi.color}`}>
                <Icon name={kpi.icon as Parameters<typeof Icon>[0]['name']} size={18} />
              </div>
              <div>
                <p className={`text-xl font-bold ${kpi.color}`}>{kpi.value}</p>
                <p className="text-xs text-muted-foreground">{kpi.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-surface-2 border border-border rounded-xl p-1 w-fit overflow-x-auto">
          {TABS.map((tab, i) => (
            <button
              key={tab}
              onClick={() => setActiveTab(i)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${activeTab === i ? 'bg-primary text-black' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 0 && (
          <div className="space-y-4">
            <div className="flex gap-3 items-center">
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="bg-surface-2 border border-border text-sm text-foreground rounded-lg px-3 py-1.5 focus:outline-none focus:border-primary">
                <option value="all">All Suppliers</option>
                <option value="live">Live</option>
                <option value="ready">Ready</option>
                <option value="awaiting_credentials">Awaiting Credentials</option>
              </select>
              <span className="text-xs text-muted-foreground ml-auto">{filtered.length} suppliers</span>
            </div>

            <div className="bg-surface-2 border border-border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Supplier</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden md:table-cell">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden lg:table-cell">API Health</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden lg:table-cell">Last Sync</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden xl:table-cell">Catalogue</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden xl:table-cell">Validation</th>
                    <th className="text-right px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map(sup => {
                    const sc = STATUS_CONFIG[sup.status];
                    const hc = HEALTH_CONFIG[sup.apiHealth];
                    return (
                      <tr key={sup.id} className="hover:bg-surface-3/40 transition-colors">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${sc.dot}`} />
                            <div>
                              <p className="font-semibold text-foreground">{sup.name}</p>
                              <p className="text-xs text-muted-foreground">{sup.category}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${sc.color}`}>{sc.label}</span>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <span className={`text-xs font-semibold ${hc.color}`}>{hc.label}</span>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <span className="text-xs text-muted-foreground">{sup.lastSync}</span>
                        </td>
                        <td className="px-4 py-3 hidden xl:table-cell">
                          <span className="text-xs text-foreground">{sup.catalogueSize > 0 ? sup.catalogueSize.toLocaleString() : '—'}</span>
                        </td>
                        <td className="px-4 py-3 hidden xl:table-cell">
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-1.5 bg-surface-3 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${sup.validationScore >= 90 ? 'bg-emerald-400' : sup.validationScore >= 60 ? 'bg-amber-400' : 'bg-red-400'}`} style={{ width: `${sup.validationScore}%` }} />
                            </div>
                            <span className="text-xs text-muted-foreground">{sup.validationScore}%</span>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button onClick={() => setSelectedSupplier(sup)} className="text-xs bg-surface-3 text-foreground px-2.5 py-1 rounded hover:bg-surface-4 transition-colors">Details</button>
                            {sup.status === 'awaiting_credentials' && (
                              <Link href="/enterprise-api-testing" className="text-xs bg-amber-500/15 text-amber-400 px-2.5 py-1 rounded hover:bg-amber-500/25 transition-colors">Test API</Link>
                            )}
                            {sup.status === 'live' && (
                              <button className="text-xs bg-blue-500/15 text-blue-400 px-2.5 py-1 rounded hover:bg-blue-500/25 transition-colors">Sync</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Health Monitor Tab */}
        {activeTab === 1 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {SUPPLIERS.map(sup => {
              const sc = STATUS_CONFIG[sup.status];
              const hc = HEALTH_CONFIG[sup.apiHealth];
              return (
                <div key={sup.id} className="bg-surface-2 border border-border rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm font-bold text-foreground">{sup.name}</p>
                      <p className="text-xs text-muted-foreground">{sup.category}</p>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${sc.color}`}>{sc.label}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-surface-3 rounded-lg p-2">
                      <p className="text-muted-foreground">API Health</p>
                      <p className={`font-bold ${hc.color}`}>{hc.label}</p>
                    </div>
                    <div className="bg-surface-3 rounded-lg p-2">
                      <p className="text-muted-foreground">Uptime</p>
                      <p className="font-bold text-foreground">{sup.uptime > 0 ? `${sup.uptime}%` : '—'}</p>
                    </div>
                    <div className="bg-surface-3 rounded-lg p-2">
                      <p className="text-muted-foreground">Response</p>
                      <p className="font-bold text-foreground">{sup.responseMs > 0 ? `${sup.responseMs}ms` : '—'}</p>
                    </div>
                    <div className="bg-surface-3 rounded-lg p-2">
                      <p className="text-muted-foreground">Last Sync</p>
                      <p className="font-bold text-foreground">{sup.lastSync}</p>
                    </div>
                  </div>
                  {sup.alerts.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {sup.alerts.map((a, i) => (
                        <div key={i} className={`text-xs px-2.5 py-1.5 rounded flex items-center gap-1.5 ${a.level === 'critical' ? 'bg-red-500/15 text-red-400' : 'bg-amber-500/15 text-amber-400'}`}>
                          <Icon name="ExclamationTriangleIcon" size={12} />
                          {a.message}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Sync & Import Tab */}
        {activeTab === 2 && (
          <div className="space-y-3">
            {SUPPLIERS.map(sup => (
              <div key={sup.id} className="bg-surface-2 border border-border rounded-xl p-4 flex items-center gap-4">
                <div className="w-36 flex-shrink-0">
                  <p className="text-sm font-semibold text-foreground">{sup.name}</p>
                  <p className="text-xs text-muted-foreground">{sup.lastSync}</p>
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">Sync Progress</span>
                    <span className="text-xs font-semibold text-foreground">{sup.syncProgress}%</span>
                  </div>
                  <div className="h-2 bg-surface-3 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${sup.syncProgress === 100 ? 'bg-emerald-400' : sup.syncProgress > 0 ? 'bg-blue-400' : 'bg-surface-4'}`} style={{ width: `${sup.syncProgress}%` }} />
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-bold text-foreground">{sup.catalogueSize > 0 ? sup.catalogueSize.toLocaleString() : '—'}</p>
                  <p className="text-xs text-muted-foreground">items</p>
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  {sup.status === 'live' || sup.status === 'ready' ? (
                    <>
                      <button className="text-xs bg-blue-500/15 text-blue-400 px-2.5 py-1 rounded hover:bg-blue-500/25 transition-colors">Full Sync</button>
                      <button className="text-xs bg-surface-3 text-foreground px-2.5 py-1 rounded hover:bg-surface-4 transition-colors">Delta</button>
                    </>
                  ) : (
                    <Link href="/enterprise-api-testing" className="text-xs bg-amber-500/15 text-amber-400 px-2.5 py-1 rounded hover:bg-amber-500/25 transition-colors">Configure</Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error Logs Tab */}
        {activeTab === 3 && (
          <div className="bg-surface-2 border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-border">
              <h3 className="text-sm font-semibold text-foreground">Error Logs</h3>
            </div>
            <div className="p-5">
              {SUPPLIERS.filter(s => s.alerts.length > 0).length === 0 ? (
                <div className="text-center py-8">
                  <Icon name="CheckCircleIcon" size={32} className="text-emerald-400 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No critical errors across live suppliers</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {SUPPLIERS.filter(s => s.alerts.length > 0).map(sup =>
                    sup.alerts.map((a, i) => (
                      <div key={`${sup.id}-${i}`} className={`flex items-start gap-3 p-3 rounded-lg ${a.level === 'critical' ? 'bg-red-500/10 border border-red-500/20' : 'bg-amber-500/10 border border-amber-500/20'}`}>
                        <Icon name="ExclamationTriangleIcon" size={16} className={a.level === 'critical' ? 'text-red-400' : 'text-amber-400'} />
                        <div>
                          <p className="text-sm font-semibold text-foreground">{sup.name}</p>
                          <p className="text-xs text-muted-foreground">{a.message}</p>
                        </div>
                        <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded ${a.level === 'critical' ? 'bg-red-500/15 text-red-400' : 'bg-amber-500/15 text-amber-400'}`}>{a.level.toUpperCase()}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Performance Tab */}
        {activeTab === 4 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {SUPPLIERS.filter(s => s.status === 'live' || s.status === 'ready').map(sup => (
              <div key={sup.id} className="bg-surface-2 border border-border rounded-xl p-4">
                <h3 className="text-sm font-bold text-foreground mb-3">{sup.name}</h3>
                <div className="grid grid-cols-3 gap-3 text-xs">
                  {[
                    { label: 'Bookings Today', value: sup.bookingsToday },
                    { label: 'Revenue Today', value: `₹${(sup.revenueToday / 1000).toFixed(0)}K` },
                    { label: 'Response Time', value: `${sup.responseMs}ms` },
                    { label: 'Uptime', value: `${sup.uptime}%` },
                    { label: 'Catalogue Size', value: sup.catalogueSize.toLocaleString() },
                    { label: 'Validation', value: `${sup.validationScore}%` },
                  ].map(m => (
                    <div key={m.label} className="bg-surface-3 rounded-lg p-2 text-center">
                      <p className="text-base font-bold text-foreground">{m.value}</p>
                      <p className="text-muted-foreground mt-0.5">{m.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Supplier Detail Modal */}
      {selectedSupplier && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setSelectedSupplier(null)}>
          <div className="bg-surface-1 border border-border rounded-2xl p-6 max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-base font-bold text-foreground">{selectedSupplier.name}</h3>
                <p className="text-xs text-muted-foreground">{selectedSupplier.category}</p>
              </div>
              <button onClick={() => setSelectedSupplier(null)} className="text-muted-foreground hover:text-foreground">
                <Icon name="XMarkIcon" size={20} />
              </button>
            </div>
            <div className="space-y-2 text-sm mb-4">
              {[
                ['Status', STATUS_CONFIG[selectedSupplier.status].label],
                ['API Health', HEALTH_CONFIG[selectedSupplier.apiHealth].label],
                ['Credentials', selectedSupplier.credentialStatus],
                ['Last Sync', selectedSupplier.lastSync],
                ['Catalogue Size', selectedSupplier.catalogueSize > 0 ? selectedSupplier.catalogueSize.toLocaleString() : 'Not synced'],
                ['Validation Score', `${selectedSupplier.validationScore}%`],
                ['Uptime', selectedSupplier.uptime > 0 ? `${selectedSupplier.uptime}%` : 'N/A'],
                ['Response Time', selectedSupplier.responseMs > 0 ? `${selectedSupplier.responseMs}ms` : 'N/A'],
                ["Today's Bookings", selectedSupplier.bookingsToday],
                ["Today's Revenue", selectedSupplier.revenueToday > 0 ? `₹${(selectedSupplier.revenueToday / 1000).toFixed(0)}K` : '₹0'],
              ].map(([k, v]) => (
                <div key={String(k)} className="flex justify-between border-b border-border/50 pb-2 last:border-0">
                  <span className="text-muted-foreground">{k}</span>
                  <span className="text-foreground font-medium">{v}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              {selectedSupplier.status === 'awaiting_credentials' && (
                <Link href="/enterprise-api-testing" className="flex-1 text-center bg-amber-500/15 text-amber-400 text-sm font-semibold py-2 rounded-lg hover:bg-amber-500/25 transition-colors">Test API</Link>
              )}
              {(selectedSupplier.status === 'live' || selectedSupplier.status === 'ready') && (
                <button className="flex-1 bg-blue-500/15 text-blue-400 text-sm font-semibold py-2 rounded-lg hover:bg-blue-500/25 transition-colors">Manual Sync</button>
              )}
              <Link href="/catalogue-import-validator" className="flex-1 text-center bg-surface-3 text-foreground text-sm font-semibold py-2 rounded-lg hover:bg-surface-4 transition-colors">Validate Catalogue</Link>
              <button onClick={() => setSelectedSupplier(null)} className="flex-1 bg-surface-3 text-foreground text-sm font-semibold py-2 rounded-lg hover:bg-surface-4 transition-colors">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
